import React from 'react';
import { Mail, Phone } from 'lucide-react';
import { getFieldSchema, getIndustryKey } from '../../constants/uiRegistry';
import { siteBrandLabel } from '../../utils/siteBrand';
import './LeadsList.css';

// Fields the card already renders (or that don't belong on a card face).
// Whatever the industry's lead schema adds beyond these renders as a
// labeled value — e.g. events: event type, event date, guest count.
const CARD_BASE_FIELDS = new Set([
  'name', 'email', 'phone_number', 'phone', 'service', 'message', 'created_at', 'notes',
]);

const humanizeField = (field) => {
  const s = String(field).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Same resolution chain as InfoTab's readFieldValue — intake parks vertical
// fields under attributes/extended_fields (or .raw after mapEntity()).
const readLeadField = (lead, field) => {
  const candidates = [
    lead?.[field],
    lead?.raw?.[field],
    lead?.extended_fields?.[field],
    lead?.raw?.extended_fields?.[field],
    lead?.attributes?.[field],
    lead?.intake_attributes?.[field],
  ];
  for (const value of candidates) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const getSchemaExtras = (lead, extraFields) =>
  extraFields
    .map((field) => {
      const value = readLeadField(lead, field);
      return value == null || typeof value === 'object'
        ? null
        : { field, label: humanizeField(field), value: String(value) };
    })
    .filter(Boolean);

const getStatusLabel = (lead) =>
  String(lead.pipelineStageLabel || lead.safePipelineStage || lead.safeStatus || lead.status || 'New');

const getCompanyLabel = (lead) =>
  lead.company_name ||
  lead.company ||
  lead.business_name ||
  lead.serviceLabel ||
  lead.service ||
  'No company';

const getPhone = (lead) => lead.displayPhone || lead.phone_number || lead.primary_phone || '';
const getEmail = (lead) => lead.displayEmail || lead.email || lead.primary_email || '';

export default function LeadsList({ leads = [], view = 'grid', onLeadClick }) {
  const extraFields = getFieldSchema('leads', getIndustryKey('general')).filter(
    (field) => !CARD_BASE_FIELDS.has(field),
  );

  return (
    <div className={`leads-list ${view === 'list' ? 'is-list' : 'is-grid'}`}>
      {leads.map((lead) => {
        const name = lead.displayName || lead.name || 'Unknown Lead';
        const status = getStatusLabel(lead);
        const company = getCompanyLabel(lead);
        const phone = getPhone(lead);
        const email = getEmail(lead);
        const extras = getSchemaExtras(lead, extraFields);
        const brand = siteBrandLabel(lead);
        const key = lead.id ?? lead.lead_id ?? lead.universal_id ?? `${name}-${company}`;

        return (
          <article
            key={key}
            className='leads-list-card'
            role='button'
            tabIndex={0}
            onClick={() => onLeadClick?.(lead)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onLeadClick?.(lead);
            }}
          >
            <div className='leads-list-card__header'>
              <h3>{name}</h3>
              <span className='leads-list-card__status'>{status}</span>
            </div>

            <div className='leads-list-card__company'>
              {brand ? <span className='lead-brand-badge'>{brand}</span> : null}
              {company}
            </div>

            <div className='leads-list-card__meta'>
              {phone ? <span>{phone}</span> : null}
              {email ? <span>{email}</span> : null}
            </div>

            {extras.length > 0 && (
              <div className='leads-list-card__meta'>
                {extras.map(({ field, label, value }) => (
                  <span key={field}>{`${label}: ${value}`}</span>
                ))}
              </div>
            )}

            <div className='leads-list-card__actions'>
              <button type='button' aria-label='Call lead' disabled={!phone}>
                <Phone size={16} />
              </button>
              <button type='button' aria-label='Email lead' disabled={!email}>
                <Mail size={16} />
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
